import React, { useState, useEffect, useRef } from "react";
import CardContainer from "../../components/card/card-container.jsx";
import Api from "../../api/global.service";
import env from "../../environment.js";
import Swal from "sweetalert2";
import { useForm, useFieldArray } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
// yup se usa sólo a través de yupResolver con esquema externo
import { ReactHookFormSelect } from "../../nuup/componentes/Forms/ReactHookFormSelect.jsx";
import { ReactHookFormInput } from "../../nuup/componentes/Forms/ReactHookFormInput.jsx";

import CalidadAnalisisFisicoZarandas from './calidad-analisis-fisico-zarandas.js';
import { useHistory } from "react-router-dom";
import { getCalidadLS } from './components/helpers.js';
import { buildDefaultValues } from './components/calidad-analisis-fisico.defaults.js';
import { schema } from './components/calidad-analisis-fisico.schema.js';
import CalidadAnalisisFisicoDefectos from './calidad-analisis-fisico-defectos.js';
import CalidadAnalisisFisicoForm from './components/calidad-analisis-fisico-form.js';
import CalidadAnalisisOro from './components/calidad-analisis-oro-form.js';
import { calcPesoCafeLimpioPorZarandas, calcRendimientoExportableDesdeLimpio } from './components/calidad-calculos.js';
import CalidadAnalisisSensorialForm from './components/calidad-analisis-sensorial-form.js';
import Accordion from '@mui/material/Accordion';
import AccordionSummary from '@mui/material/AccordionSummary';
import AccordionDetails from '@mui/material/AccordionDetails';
import Badge from '@mui/material/Badge';
import { getJSON } from '../../utils/localStorageJSON.js';

const CalidadAnalisisFisico = () => {
  const queryParameters = new URLSearchParams(window.location.search)
  const history = useHistory();
  const [data, setData] = useState([]);
  const [ciclo, setCiclo] = useState({});
  const [blocking, setBlocking] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [oroExpanded, setOroExpanded] = useState(false);
  const [showAccordErrorBadges, setShowAccordErrorBadges] = useState(false);
  // Recuerda último valor calculado automáticamente para no sobreescribir valores editados por el usuario
  const prevAutoOroRef = useRef({ peso: undefined, rendimiento: undefined });

  // Ciclo seleccionado (usado para enviar en info_muestra)
  const cicloIdLS = Number(localStorage.getItem(`${env.appName}-ciclo_id`) || 0);
  const [defaultValues] = useState(() => buildDefaultValues());

  // Esquema y validadores movidos a módulo externo

  const {
    register,
    handleSubmit,
    formState: { errors },
    control,
    setValue,
    watch,
    getValues,
    clearErrors,
    setError,
    reset
  } = useForm({
    resolver: yupResolver(schema),
    mode: "all",
    defaultValues,
  });

  // Detectar modo edición si existe algún identificador (ID o UUID)
  const idCalidadAnalisisWatch = watch('info_muestra.id_calidad_analisis');
  const uuidCalidadAnalisisWatch = watch('info_muestra.uuid_calidad_analisis');
  const uuidWatch = watch('info_muestra.uuid');
  useEffect(() => {
    const idFromLS = getCalidadLS('info_muestra.id_calidad_analisis');
    const uuidFromLS = getCalidadLS('info_muestra.uuid_calidad_analisis') || getCalidadLS('info_muestra.uuid');
    const hasIdentifier = Boolean(
      idFromLS || uuidFromLS || defaultValues?.info_muestra?.id_calidad_analisis || defaultValues?.info_muestra?.uuid_calidad_analisis || defaultValues?.info_muestra?.uuid
    );
    setIsEditing(hasIdentifier);
  }, []);

  useEffect(() => {
    const hasIdentifier = Boolean(idCalidadAnalisisWatch || uuidCalidadAnalisisWatch || uuidWatch);
    if (hasIdentifier) setIsEditing(true);
  }, [idCalidadAnalisisWatch, uuidCalidadAnalisisWatch, uuidWatch]);

  // Vincular oro_2 con peso oro del análisis físico
  const pesoOroFisicoWatch = watch('analisis_fisico.pergamino_peso_muestra_oro');
  useEffect(() => {
    const val = getValues()?.analisis_fisico?.pergamino_peso_muestra_oro;
    setValue('analisis_oro.pergamino_peso_muestra_oro_2', val ?? '', { shouldValidate: true, shouldTouch: false, shouldDirty: true });
    if (val) clearErrors('analisis_oro.pergamino_peso_muestra_oro_2');
  }, [pesoOroFisicoWatch]);

  const setFilter = (value) => {
    setCiclo(value.target);
  };

  const { fields, append, remove, insert } = useFieldArray({
    control,
    name: "analisis_oro.zarandas",
  });

  const agregaZaranda = () => {
    append({
      id_zaranda: 0,
      valor: ""
    });
  };

  const eliminaZaranda = (index) => {
    remove(index);
  }

  const onSubmit = values => {
    console.log('onSubmit');
    console.log(values);
  };

  const sendData = async (values) => {
    // El formulario ya está anidado según el nuevo formato
    // Calcular puntaje en taza antes de enviar
    const n = (v) => {
      const num = Number(v);
      return isNaN(num) ? 0 : num;
    };
    const asv = values?.analisis_sensorial || {};
    const sumaAtributos =
      n(asv.fragancia_aroma) +
      n(asv.sabor) +
      n(asv.sabor_residual) +
      n(asv.acidez) +
      n(asv.cuerpo) +
      n(asv.balance) +
      n(asv.taza_limpia) * 2 +
      n(asv.uniformidad) * 2 +
      n(asv.dulzor) * 2 +
      n(asv.catador);
    const defectos = n(asv.defectos_no_tazas) * n(asv.defectos_intensidad);
    const puntajeCalculado = Number((sumaAtributos - defectos).toFixed(2));

    // Incluir "analisis_oro" solo si el usuario desplegó la sección o eligió datos
    const oroVals = values?.analisis_oro || {};
    const fisicoVals = values?.analisis_fisico || {};
    const hasColor = fisicoVals?.id_color_muestra != null && String(fisicoVals.id_color_muestra).trim() !== '';
    const zItems = Array.isArray(oroVals?.zarandas) ? oroVals.zarandas : [];
    const hasZarandas = zItems.some(z => (Number(z?.id_zaranda) > 0) || (String(z?.valor || '').trim() !== ''));
    const prim = Array.isArray(oroVals?.defectos_primarios) ? oroVals.defectos_primarios : [];
    const sec = Array.isArray(oroVals?.defectos_secundarios) ? oroVals.defectos_secundarios : [];
  const hasDefectos = [...prim, ...sec].some(d => (Number(d?.id_tipo_mancha) > 0) || (String(((d?.no_granos ?? d?.granos) || '')).trim() !== '') || (String(d?.defectos || '').trim() !== ''));
    const hasPesoOro2 = String(oroVals?.pergamino_peso_muestra_oro_2 ?? '').trim() !== '';
    const shouldIncludeOro = Boolean(oroExpanded || hasZarandas || hasDefectos || hasPesoOro2);

    const payload = {
      info_muestra: {
        ...values.info_muestra,
      },
      analisis_fisico: {
        ...values.analisis_fisico,
      },
      analisis_sensorial: {
        ...values.analisis_sensorial,
        puntaje_calculado: puntajeCalculado,
      },
    };
    if (shouldIncludeOro) {
      // Mapear claves UI -> API para defectos (usar 'defectos' directamente)
      const mapUiDefectoToApi = (d) => ({
        ...d,
        no_granos: d?.granos ?? d?.no_granos ?? '',
        defectos: d?.defectos ?? '',
      });
      const oroDef = values?.analisis_oro || {};
      const zarandas = Array.isArray(oroDef?.zarandas) ? oroDef.zarandas.filter((z) => {
        const hasId = Number(z?.id_zaranda) > 0;
        const hasVal = String(z?.valor ?? '').trim() !== '';
        return hasId && hasVal;
      }) : [];
      const defectosPrim = Array.isArray(oroDef?.defectos_primarios) ? oroDef.defectos_primarios
        .map(mapUiDefectoToApi)
        .filter((d) => {
          const hasTipo = Number(d?.id_tipo_mancha) > 0;
          const hasGranos = String((d?.no_granos ?? '').toString().trim()) !== '';
          const hasDef = String((d?.defectos ?? '').toString().trim()) !== '';
          return hasTipo && hasGranos && hasDef;
        }) : [];
      const defectosSec = Array.isArray(oroDef?.defectos_secundarios) ? oroDef.defectos_secundarios
        .map(mapUiDefectoToApi)
        .filter((d) => {
          const hasTipo = Number(d?.id_tipo_mancha) > 0;
          const hasGranos = String((d?.no_granos ?? '').toString().trim()) !== '';
          const hasDef = String((d?.defectos ?? '').toString().trim()) !== '';
          return hasTipo && hasGranos && hasDef;
        }) : [];

      payload.analisis_oro = {
        ...oroDef,
        zarandas,
        defectos_primarios: defectosPrim,
        defectos_secundarios: defectosSec,
      };
    }

    try {
      const id_calidad_analisis = values?.info_muestra?.id_calidad_analisis || getCalidadLS('info_muestra.id_calidad_analisis');
      const uuid_calidad_analisis = values?.info_muestra?.uuid_calidad_analisis || getCalidadLS('info_muestra.uuid_calidad_analisis');
      const uuid = values?.info_muestra?.uuid || getCalidadLS('info_muestra.uuid');
      // Para actualización, el backend requiere uuid_calidad_analisis (no ID numérico)
      const identifier = uuid_calidad_analisis || uuid || id_calidad_analisis;

      if (identifier) {
        // Actualiza registro unificado usando UUID preferentemente
        const updateResp = await Api.update('calidad/analisis', identifier, payload);
        const viewUuid = uuid_calidad_analisis || uuid || updateResp?.data?.data?.uuid_calidad_analisis || getCalidadLS('info_muestra.uuid_calidad_analisis');

        // Preguntar acción posterior: ver análisis, continuar editando o regresar
        const result = await Swal.fire({
          icon: 'question',
          title: '¿Qué deseas hacer?',
          text: 'Puedes ver la visualización del análisis o regresar.',
          showDenyButton: true,
          showCancelButton: false,
          confirmButtonText: 'Ver análisis',
          denyButtonText: 'Regresar al listado'
        });

        if (result.isConfirmed && viewUuid) {
          history.push({ pathname: `/admin/calidad/analisis/visualizacion/${viewUuid}` });
        } else if (result.isDenied) {
          history.push({ pathname: '/admin/calidad' });
        }
      } else {
        // Crea registro unificado
        const response = await Api.create('calidad/analisis', payload);
        let configAnalisis = getJSON(`${env.appName}-calidad`, {});
        configAnalisis.id_calidad_analisis = response.data?.data?.id_calidad_analisis || configAnalisis.id_calidad_analisis;
        configAnalisis.id_calidad_analisis_fisico = response.data?.data?.id_calidad_analisis_fisico || configAnalisis.id_calidad_analisis_fisico;
        configAnalisis.id_calidad_analisis_sensorial = response.data?.data?.id_calidad_analisis_sensorial || configAnalisis.id_calidad_analisis_sensorial;
        // Guardar también UUID si el API lo devuelve
        configAnalisis.uuid_calidad_analisis = response.data?.data?.uuid_calidad_analisis || configAnalisis.uuid_calidad_analisis;
        localStorage.setItem(`${env.appName}-calidad`, JSON.stringify(configAnalisis));

        const viewUuid = response?.data?.data?.uuid_calidad_analisis || configAnalisis.uuid_calidad_analisis;

        // Preguntar acción posterior tras crear
        const result = await Swal.fire({
          icon: 'question',
          title: '¿Qué deseas hacer?',
          text: 'Puedes ver la visualización del análisis o regresar.',
          showDenyButton: true,
          showCancelButton: false,
          confirmButtonText: 'Ver análisis',
          denyButtonText: 'Regresar al listado'
        });

        if (result.isConfirmed && viewUuid) {
          history.push({ pathname: `/admin/calidad/analisis/visualizacion/${viewUuid}` });
        } else if (result.isDenied) {
          history.push({ pathname: '/admin/calidad' });
        }
      }

      setBlocking(false);
      // No forzamos navegación adicional aquí; ya se maneja por la alerta
    } catch (e) {
      console.log(e);
      setBlocking(false);
      Swal.fire({
        icon: 'error',
        title: '',
        text: e.response?.data?.message || 'Error',
      });
    }
  };

  const validateHandler = () => {
    let data = getValues();

    schema.validate(data, { abortEarly: false })
      .then(function (valid) {
        // Bloquear el botón únicamente después de que la validación sea exitosa
        setBlocking(true);
        sendData(data);
        clearErrors();
        setShowAccordErrorBadges(false);
      }).catch(function (err) {
        err.inner.reduce(function (map, obj) {
          setError(obj.path, { message: obj.message });
          map[obj.path] = obj.message;
          return map;
        }, {});
        setBlocking(false);
        setShowAccordErrorBadges(true);
      });
  }

  // Contabilizar errores por sección del acordeón
  const countErrors = (node) => {
    if (!node) return 0;
    if (Array.isArray(node)) return node.reduce((sum, n) => sum + countErrors(n), 0);
    if (typeof node === 'object') {
      let total = node?.message ? 1 : 0;
      for (const key of Object.keys(node)) {
        const val = node[key];
        if (val && typeof val === 'object') total += countErrors(val);
      }
      return total;
    }
    return 0;
  };

  const fisicoErrorCount = countErrors(errors?.analisis_fisico);
  const oroErrorCount = countErrors(errors?.analisis_oro);
  const sensorialErrorCount = countErrors(errors?.analisis_sensorial);

  return (
    <div>
      {isEditing && (
        <div className="alert alert-warning mt-3" role="alert" style={{ borderRadius: 8 }}>
          <strong>Modo edición:</strong> estás editando un análisis existente.
        </div>
      )}
      <div>
        <button
          className="btn btn-outline-dark mt-3"
          type="button"
          onClick={() => history.push({ pathname: '/admin/calidad/seleccion', })}
        >
          <i className="fa fa-chevron-right-left"></i>
          {" Volver a tipo de muestra"}
        </button>
      </div>

      <CardContainer height={"100%"} style={{ marginTop: 10}}>
        {/* Registro de muestra (fuera del acordeón) */}
        <div className="row mt-3">
          <div className="col-lg-12" style={{ display: 'flex'}}>
            <h4>{"Registro de muestra"}</h4>
          </div>
        </div>

        <div className="row">

          <div className="col-xs-4 col-lg-4 mt-2">
              <ReactHookFormInput
                type="text"
                id="info_muestra.id_muestra"
                name="info_muestra.id_muestra"
                label="ID Muestra"
                error={errors?.info_muestra?.id_muestra}
                control={control}
                value={getValues()?.info_muestra?.id_muestra}
              />
            </div>
          <div className="col-xs-4 col-lg-4 mt-2">
            <ReactHookFormInput
              type="date"
              id="info_muestra.fecha"
              name="info_muestra.fecha"
              label=""
              placeholder=""
              error={errors?.info_muestra?.fecha}
              control={control}
              value={getValues()?.info_muestra?.fecha}
            />
          </div>

          <div className="col-xs-4 col-lg-4 mt-2">
            <ReactHookFormSelect
              placeholder="Tipo de proceso *"
              label="Tipo de proceso *"
              id={'info_muestra.id_tipo_proceso'}
              name={'info_muestra.id_tipo_proceso'}
              error={errors?.info_muestra?.id_tipo_proceso ? true : false}
              url={"calidad/catalogos/tipo_proceso"}
              keyArray="id"
              labelArray="nombre"
              apiSearch={false}
              control={control}
              value={getValues()?.info_muestra?.id_tipo_proceso}
            />
            {errors?.info_muestra?.id_tipo_proceso && (
              <div className="invalid-feedback">
                {errors?.info_muestra?.id_tipo_proceso.message}
              </div>
            )}
          </div>
          </div>
          <div className="row">

          <div className="col-xs-4 col-lg-4 mt-2">
            <ReactHookFormSelect
              placeholder="Tipo de café *"
              label="Tipo de café *"
              id={'info_muestra.id_tipo_cafe'}
              name={'info_muestra.id_tipo_cafe'}
              error={errors?.info_muestra?.id_tipo_cafe ? true : false}
              url={"calidad/catalogos/tipo_cafe"}
              keyArray="id"
              labelArray="nombre"
              apiSearch={false}
              control={control}
              value={getValues()?.info_muestra?.id_tipo_cafe}
            />
            {errors?.info_muestra?.id_tipo_cafe && (
              <div className="invalid-feedback">
                {errors?.info_muestra?.id_tipo_cafe.message}
              </div>
            )}
          </div>

          <div className="col-xs-4 col-lg-4 mt-2">
            <ReactHookFormInput
              type="number"
              id="info_muestra.peso_asociado"
              name="info_muestra.peso_asociado"
              label="Peso asociado"
              error={errors?.info_muestra?.peso_asociado}
              control={control}
              value={getValues()?.info_muestra?.peso_asociado}
            />

            {errors?.info_muestra?.peso_asociado && (
              <div className="invalid-feedback">
                {errors?.info_muestra?.peso_asociado.message}
              </div>
            )}

          </div>
        </div>

        {/* Sección: Análisis físico */}
        <Accordion sx={{ marginTop:2 }}>
          <AccordionSummary style={{ backgroundColor: '#f5f5f5' }} expandIcon={<i className="fa fa-chevron-right" aria-hidden="true"></i>}>
            <div style={{ display: 'flex', alignItems: 'center', flex: '1 1 auto' }}>
              <h5 className="m-0" style={{ fontSize: 16 }}>Análisis físico</h5>
              {showAccordErrorBadges && fisicoErrorCount > 0 && (
                <div style={{ marginLeft: 'auto' }}>
                  <Badge badgeContent={fisicoErrorCount} color="error" />
                </div>
              )}
            </div>
          </AccordionSummary>
          <AccordionDetails>
            <CalidadAnalisisFisicoForm {...{ control, errors, getValues, watch, onOroDerivedRecalc: () => {
              const oroVals = getValues()?.analisis_oro || {};
              const entries = Array.isArray(oroVals?.zarandas) ? oroVals.zarandas : [];
              const totalDefectosGr = oroVals?.total_defectos_gramos;
              const pesoMuestra = Number(getValues()?.analisis_fisico?.pergamino_peso_muestra_pergamino);
              const limpio = calcPesoCafeLimpioPorZarandas(entries, totalDefectosGr, [1]);
              const rendimiento = calcRendimientoExportableDesdeLimpio(pesoMuestra, limpio);
              const currPeso = getValues()?.analisis_oro?.peso_cafe_limpio_grs;
              const currRend = getValues()?.analisis_oro?.rendimiento_exportable_por;
              if (limpio !== '' && !isNaN(Number(limpio))) {
                prevAutoOroRef.current.peso = Number(Number(limpio).toFixed(2));
              }
              if (rendimiento !== '' && !isNaN(Number(rendimiento))) {
                prevAutoOroRef.current.rendimiento = Number(Number(rendimiento).toFixed(2));
              }
            } }} />
          </AccordionDetails>
        </Accordion>

        {/* Sección: Análisis oro */}
        <Accordion expanded={oroExpanded} onChange={(e, isExp) => setOroExpanded(Boolean(isExp))}>
          <AccordionSummary style={{ backgroundColor: '#f5f5f5' }} expandIcon={<i className="fa fa-chevron-right" aria-hidden="true"></i>}>
            <div style={{ display: 'flex', alignItems: 'center', flex: '1 1 auto' }}>
              <h5 className="m-0" style={{ fontSize: 16 }}>Análisis oro</h5>
              {showAccordErrorBadges && oroErrorCount > 0 && (
                <div style={{ marginLeft: 'auto' }}>
                  <Badge badgeContent={oroErrorCount} color="error" />
                </div>
              )}
            </div>
          </AccordionSummary>
          <AccordionDetails>
            <CalidadAnalisisOro {...{ control, errors, getValues, register, defaultValues, setValue, onOroDerivedRecalc: () => {
              const oroVals = getValues()?.analisis_oro || {};
              const entries = Array.isArray(oroVals?.zarandas) ? oroVals.zarandas : [];
              const totalDefectosGr = oroVals?.total_defectos_gramos;
              const pesoMuestra = Number(getValues()?.analisis_fisico?.pergamino_peso_muestra_pergamino);
              const limpio = calcPesoCafeLimpioPorZarandas(entries, totalDefectosGr, [1]);
              const rendimiento = calcRendimientoExportableDesdeLimpio(pesoMuestra, limpio);
              const currPeso = getValues()?.analisis_oro?.peso_cafe_limpio_grs;
              const currRend = getValues()?.analisis_oro?.rendimiento_exportable_por;
              // No rellenar automáticamente: sólo calcular para referencia (labels)
              if (limpio !== '' && !isNaN(Number(limpio))) {
                prevAutoOroRef.current.peso = Number(Number(limpio).toFixed(2));
              }
              if (rendimiento !== '' && !isNaN(Number(rendimiento))) {
                prevAutoOroRef.current.rendimiento = Number(Number(rendimiento).toFixed(2));
              }
            } }} />
          </AccordionDetails>
        </Accordion>

        {/* Sección: Análisis sensorial */}
        <Accordion>
          <AccordionSummary style={{ backgroundColor: '#f5f5f5' }} expandIcon={<i className="fa fa-chevron-right" aria-hidden="true"></i>}>
            <div style={{ display: 'flex', alignItems: 'center', flex: '1 1 auto' }}>
              <h5 className="m-0" style={{ fontSize: 16 }}>Análisis sensorial</h5>
              {showAccordErrorBadges && sensorialErrorCount > 0 && (
                <div style={{ marginLeft: 'auto' }}>
                  <Badge badgeContent={sensorialErrorCount} color="error" />
                </div>
              )}
            </div>
          </AccordionSummary>
          <AccordionDetails>
            <CalidadAnalisisSensorialForm {...{ control, errors, getValues, watch, setValue }} />
          </AccordionDetails>
        </Accordion>

        {/* Observaciones (al final, después del acordeón) */}
        <div className="row mt-3">
          <div className="col-lg-12">
            <ReactHookFormInput
              type="text"
              id="info_muestra.observaciones"
              name="info_muestra.observaciones"
              label="Observaciones"
              placeholder=""
              error={errors?.info_muestra?.observaciones}
              control={control}
              value={getValues()?.info_muestra?.observaciones}
              multiline
            />
          </div>
        </div>

        <div className="row mt-5">
          <div className="col-xs-6 col-xl-3 col-xxl-2">
            <button
              onClick={() => validateHandler()}
              disabled={blocking}
              style={{
                borderRadius: "20px 20px 20px 20px",
                color: "#333",
                fontWeight: "normal",
                fontSize: 16,
              }}
              className="btn btn-sirio"
            >
              {'Guardar'}
            </button>
          </div>
        </div>

      </CardContainer>
    </div>
  );
};

export default CalidadAnalisisFisico;
